import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import TomSelect from "tom-select/dist/js/tom-select.complete.js"

export default class extends Controller {
  static values = {
    options: Array,
    url: String,
    createUrl: String,
    placeholder: String,
    createText: String,
    noResultsText: String
  }

  connect() {
    this.element.setAttribute("autocomplete", "random")

    const config = {
      valueField: "value",
      labelField: "text",
      searchField: ["text", "phone", "email"],
      maxItems: 1,
      placeholder: this.placeholderValue,
      create: (input, _callback) => this.openCreateModal(input),
      load: (query, callback) => this.loadSuppliers(query, callback),
      loadThrottle: 400,
      render: {
        option: (data, escape) => this.renderOption(data, escape),
        item: (data, escape) => this.renderOption(data, escape),
        option_create: () => `<div class="create text-primary-700 underline-offset-2 hover:underline">${this.escapeHtml(this.createTextValue)}</div>`,
        no_results: () => `<div class="no-results">${this.escapeHtml(this.noResultsTextValue)}</div>`
      },
      onItemAdd() {
        this.clearOptions()
        this.blur()
      },
      onItemRemove() {
        this.clearOptions()
        this.focus()
      }
    }

    if (this.hasOptionsValue) {
      config.options = this.optionsValue
    }

    this.select = new TomSelect(this.element, config)
    this.select.wrapper.classList.add("tom-select-input")
    this.replaceSelectionBeforeInput = this.replaceSelectionBeforeInput.bind(this)
    this.editSelectionOnPointerDown = this.editSelectionOnPointerDown.bind(this)
    this.select.control.addEventListener("mousedown", this.editSelectionOnPointerDown, true)
    this.select.control.addEventListener("keydown", this.replaceSelectionBeforeInput, true)
    this.select.control.addEventListener("paste", this.replaceSelectionBeforeInput, true)
  }

  disconnect() {
    this.select?.control.removeEventListener("mousedown", this.editSelectionOnPointerDown, true)
    this.select?.control.removeEventListener("keydown", this.replaceSelectionBeforeInput, true)
    this.select?.control.removeEventListener("paste", this.replaceSelectionBeforeInput, true)
    this.select?.destroy()
  }

  editSelectionOnPointerDown(event) {
    if (!this.hasSelectedItem()) return

    event.preventDefault()
    this.replaceSelectedItemWith(this.selectedEditText())
  }

  replaceSelectionBeforeInput(event) {
    if (!this.hasSelectedItem()) return

    if (event.type === "paste") {
      event.preventDefault()
      this.replaceSelectedItemWith(event.clipboardData?.getData("text") || "")
      return
    }

    if (event.metaKey || event.ctrlKey || event.altKey) return

    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault()
      this.clearSelectedItem()
      return
    }

    if (event.key.length !== 1) return

    event.preventDefault()
    this.replaceSelectedItemWith(event.key)
  }

  hasSelectedItem() {
    return this.select && this.select.items.length > 0
  }

  clearSelectedItem() {
    this.select.clear()
    this.select.clearOptions()
    this.select.inputState()
    this.select.refreshState()
    this.select.focus()
  }

  replaceSelectedItemWith(value) {
    this.clearSelectedItem()
    this.select.setTextboxValue(value)
    this.select.lastValue = ""
    this.select.onInput()
  }

  selectedEditText() {
    const selectedValue = this.select.getValue()
    const selectedOption = this.select.options[selectedValue]

    return selectedOption?.text || ""
  }

  async loadSuppliers(query, callback) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("query", query.trim())

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok) {
      callback()
      return
    }

    const data = await response.json()
    callback(data.suppliers || [])
  }

  openCreateModal(input, _callback) {
    const url = new URL(this.createUrlValue, window.location.origin)
    url.searchParams.set("title", input)
    this.resetSelectState()
    Turbo.visit(url.toString(), { frame: "modal" })
  }

  resetSelectState() {
    this.select?.clearOptions()
    this.select?.setTextboxValue("")
    this.select?.close()
    this.select?.blur()
  }

  renderOption(data, escape) {
    const details = [data.phone, data.email].filter(Boolean).join(" / ")
    const separatorHtml = details.length > 0 ? '<span class="shrink-0 text-gray-300">·</span>' : ""
    const detailsHtml = details.length > 0 ? `<span class="shrink-0 text-xs text-gray-500">${escape(details)}</span>` : ""

    return `
      <div class="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
        <span class="truncate font-medium text-gray-900">${escape(data.text)}</span>
        ${separatorHtml}
        ${detailsHtml}
      </div>
    `
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
